import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaUsers,
  FaHistory,
  FaHome,
  FaCalendarCheck,
  FaRupeeSign,
  FaBell,
  FaArrowRight,
} from 'react-icons/fa';
import { useWorkspace } from '../../context/WorkspaceContext';
import WorkspaceStaffPanel from './WorkspaceStaffPanel';

const MODULES = [
  {
    id: 'staff',
    to: '/workspace/staff',
    icon: FaUsers,
    title: 'Staff management',
    desc: 'Invite links, authorized emails & team roles',
    permission: 'manage_staff',
    statKey: 'staffCount',
  },
  {
    id: 'activity',
    to: '/workspace/activity',
    icon: FaHistory,
    title: 'Activity & logs',
    desc: 'Monitor all workspace actions',
    permission: 'view_activity',
  },
  {
    id: 'properties',
    to: '/workspace/properties',
    icon: FaHome,
    title: 'Property listings',
    desc: 'View and manage company stay listings',
    permission: 'view_properties',
    statKey: 'propertyCount',
  },
  {
    id: 'bookings',
    to: '/workspace/bookings',
    icon: FaCalendarCheck,
    title: 'Booking management',
    desc: 'Review and update booking status',
    permission: 'manage_bookings',
    statKey: 'totalBookings',
  },
  {
    id: 'revenue',
    to: '/workspace/analytics',
    icon: FaRupeeSign,
    title: 'Revenue & analytics',
    desc: 'Earnings, commissions & payment reports',
    permission: 'view_analytics',
    statKey: 'revenue',
  },
  {
    id: 'notifications',
    to: '/workspace/notifications',
    icon: FaBell,
    title: 'Notifications & reports',
    desc: 'Company alerts and booking updates',
    permission: 'view_notifications',
  },
];

const WorkspaceDashboard = () => {
  const { loading, error, stats, company, permissions } = useWorkspace();

  const visibleModules = useMemo(
    () => MODULES.filter((m) => permissions[m.permission]),
    [permissions]
  );

  const getStat = (mod) => {
    if (!stats) return null;
    if (mod.statKey === 'staffCount') return `${stats.staffCount} members`;
    if (mod.statKey === 'propertyCount') return `${stats.propertyCount} listings`;
    if (mod.statKey === 'totalBookings') return `${stats.totalBookings} bookings`;
    if (mod.statKey === 'revenue') {
      return `₹${Number(stats.revenue?.netBalance || 0).toLocaleString('en-IN')}`;
    }
    return null;
  };

  if (error) return <p className="workspace-alert error">{error}</p>;
  if (loading || !stats) return <p className="workspace-loading">Loading workspace...</p>;

  return (
    <div className="workspace-dashboard">
      <header className="workspace-dashboard-header">
        <h1 className="workspace-page-title">Company performance dashboard</h1>
        <p className="workspace-page-subtitle">
          Central hub for your company — team, listings, bookings & revenue.
        </p>
      </header>

      {permissions?.manage_staff && (
        <WorkspaceStaffPanel companyInviteCode={company?.inviteCode} />
      )}

      <div className="workspace-grid workspace-kpi-row">
        <div className="workspace-card workspace-kpi">
          <h3>Properties</h3>
          <div className="value">{stats.propertyCount}</div>
        </div>
        <div className="workspace-card workspace-kpi">
          <h3>Team members</h3>
          <div className="value">{stats.staffCount}</div>
        </div>
        <div className="workspace-card workspace-kpi">
          <h3>Total bookings</h3>
          <div className="value">{stats.totalBookings}</div>
        </div>
        <div className="workspace-card workspace-kpi">
          <h3>Net revenue</h3>
          <div className="value">
            ₹{Number(stats.revenue?.netBalance || 0).toLocaleString('en-IN')}
          </div>
        </div>
      </div>

      <h2 className="workspace-section-heading">All workspace features</h2>
      <div className="workspace-hub-grid">
        {visibleModules.map((mod) => (
          <Link key={mod.id} to={mod.to} className="workspace-hub-card">
            <div className="workspace-hub-icon">
              <mod.icon />
            </div>
            <div className="workspace-hub-body">
              <h3>{mod.title}</h3>
              <p>{mod.desc}</p>
              {getStat(mod) && <span className="workspace-hub-stat">{getStat(mod)}</span>}
            </div>
            <FaArrowRight className="workspace-hub-arrow" />
          </Link>
        ))}
      </div>

      <div className="workspace-card" style={{ marginTop: 24 }}>
        <h3>Booking status breakdown</h3>
        <div className="workspace-badge-row">
          {Object.keys(stats.bookingsByStatus || {}).length === 0 ? (
            <span className="workspace-badge">No bookings yet</span>
          ) : (
            Object.entries(stats.bookingsByStatus || {}).map(([status, count]) => (
              <span key={status} className="workspace-badge">
                {status}: {count}
              </span>
            ))
          )}
        </div>
      </div>

      {permissions?.view_activity && (stats.recentActivity || []).length > 0 && (
        <div className="workspace-table-wrap" style={{ marginTop: 24 }}>
          <div className="workspace-table-header">
            <h3>Recent activity</h3>
            <Link to="/workspace/activity">View all logs →</Link>
          </div>
          <ul className="workspace-activity-list">
            {(stats.recentActivity || []).map((row) => (
              <li key={row._id}>
                <span className="workspace-badge">{row.action}</span>
                <span>{row.summary}</span>
                <small>{new Date(row.createdAt).toLocaleString()}</small>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default WorkspaceDashboard;
